/**
 * Upload namespace registry — single source of truth for all direct-to-Blob
 * upload kinds in the app.
 *
 * Cross-compatible module: no "use client", no server-only imports. Importable
 * from both client components (size checks, path building) and server code
 * (presign, verification, sweep). Satisfies Critical Rule 5.
 *
 * Each namespace owns its Blob path prefix, size cap, allowed content types,
 * retention policy, and auth strategy. The presign factory
 * (src/services/server/upload/presignService.ts) and the retention sweep
 * (src/lib/inngest/sweepExpiredUploadBlobs.ts) consume this registry.
 *
 * See ADR 037 (docs/decisions/037-blob-uploads-namespace-service.md) for the
 * design rationale and abuse-ceiling analysis.
 */

// ---------------------------------------------------------------------------
// Constants moved here from domain modules (ADR-013 dependency direction:
// registry must NOT import from Labs). The old locations re-export from here.
// ---------------------------------------------------------------------------

/** Hard ceiling for a CSV import blob, enforced at the token and server-side. */
export const MAX_CSV_IMPORT_BYTES = 20_000_000;

/** Hard ceiling for an uploaded pitch deck, enforced at the token and server-side. */
export const MAX_PITCH_DECK_BYTES = 50 * 1024 * 1024;

export const PITCH_DECK_CONTENT_TYPES = [
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
] as const;

// ---------------------------------------------------------------------------
// Registry types
// ---------------------------------------------------------------------------

export type UploadRetention = "48h" | "indefinite";
export type UploadNamespaceAuth = "clerk" | "onboarding-session";

export interface UploadNamespaceConfig {
  /** Blob pathname root. The sweep scans this prefix; per-principal subpaths live under it. */
  readonly pathPrefix: string;
  readonly maxBytes: number;
  readonly allowedContentTypes: readonly string[];
  /**
   * "48h" namespaces are reclaimed by the daily sweep within ~48h; the
   * PER-NAMESPACE lifecycle varies and the individual config comment is
   * authoritative. Most are parse-and-discard (delete-after-processing, sweep as a
   * backstop); some (e.g. `task-evidence-decks`) are RETAINED for the window so a
   * download route can serve the blob, and the sweep is their SOLE cleanup owner.
   * "indefinite" namespaces are structurally excluded from the sweep.
   */
  readonly retention: UploadRetention;
  /** How the presign route authenticates the uploader and derives the allowed path prefix. */
  readonly auth: UploadNamespaceAuth;
}

// ---------------------------------------------------------------------------
// Registry
// ---------------------------------------------------------------------------

export const UPLOAD_NAMESPACES = {
  "people-csv": {
    pathPrefix: "people-imports/",
    maxBytes: MAX_CSV_IMPORT_BYTES,
    allowedContentTypes: ["text/csv", "text/plain", "application/vnd.ms-excel"],
    retention: "48h",
    auth: "clerk",
  },
  "pitch-decks": {
    pathPrefix: "pitch-decks/",
    maxBytes: MAX_PITCH_DECK_BYTES,
    allowedContentTypes: PITCH_DECK_CONTENT_TYPES,
    retention: "48h",
    auth: "clerk",
  },
  // Fundraising decks. The FIRST `indefinite` namespace, which ADR 037
  // reserved for user-browsable files — this is that case. Every other deck
  // namespace is parse-and-discard (48h, swept), but these are versions a
  // founder returns to: "generate a 5-minute cut", "send the one I showed
  // Menlo", "attach the current deck to this intro email". A swept blob would
  // turn a working deck history into dead links, so the sweep must structurally
  // skip this prefix rather than merely be slow to reach it.
  //
  // Holds BOTH the founder's uploaded source deck and each generated version,
  // separated by subpath, so a better parser can re-extract from the original
  // later instead of being stuck with whatever the first pass understood.
  "investor-decks": {
    pathPrefix: "investor-decks/",
    maxBytes: MAX_PITCH_DECK_BYTES,
    allowedContentTypes: PITCH_DECK_CONTENT_TYPES,
    retention: "indefinite",
    auth: "clerk",
  },
  "onboarding-decks": {
    pathPrefix: "onboarding-materials/",
    maxBytes: 30 * 1024 * 1024,
    allowedContentTypes: PITCH_DECK_CONTENT_TYPES,
    retention: "48h",
    auth: "onboarding-session",
  },
  // tasks-v4 (TASKS_V4): a founder uploads a deck/doc as a task's `deckOrDoc`
  // proof from the task lightbox. Temporary retention (48h, swept): the blob is
  // read for facts scoped to the project AND kept for the 48h window so the
  // authenticated evidence-download route (`/api/task-evidence-deck/download/
  // [actionId]`) and the Activity-feed pill can serve the original file. The
  // parse job NEVER deletes it — the daily sweep (sweepExpiredUploadBlobs) is the
  // sole cleanup owner. After the sweep the facts, not the file, are the durable
  // artifact (unlike `investor-decks`, which a founder returns to indefinitely).
  // Clerk-authenticated, path pinned per user.
  "task-evidence-decks": {
    pathPrefix: "task-evidence-decks/",
    maxBytes: 30 * 1024 * 1024,
    allowedContentTypes: PITCH_DECK_CONTENT_TYPES,
    retention: "48h",
    auth: "clerk",
  },
} as const satisfies Record<string, UploadNamespaceConfig>;

export type UploadNamespaceKey = keyof typeof UPLOAD_NAMESPACES;

/**
 * Prefix a Clerk-authenticated founder may upload a task-evidence deck under
 * (tasks-v4). User-scoped, not project-scoped — one founder cannot stage under
 * another's prefix. The presign route separately gates on the PROJECT owner's
 * flag (via the client's `clientPayload.projectId`), but the prefix stays keyed
 * to the authenticated uploader: the project only decides whether the feature is
 * on, never where the blob lands. Single-sourced because THREE places must agree
 * byte-for-byte: the client pathname builder, the presign route's allowed
 * prefix, and the parse job's consume-time check.
 */
export function taskEvidenceDeckBlobPrefixForUser(appUserId: string): string {
  return `${UPLOAD_NAMESPACES["task-evidence-decks"].pathPrefix}${appUserId}/`;
}

/** True when a pathname belongs to this user's task-evidence-deck namespace. */
export function isTaskEvidenceDeckPathForUser(
  pathname: string,
  appUserId: string,
): boolean {
  return pathname.startsWith(taskEvidenceDeckBlobPrefixForUser(appUserId));
}

/**
 * Sub-prefix for anonymous client-uploaded onboarding decks. Single-sourced
 * because THREE places must agree byte-for-byte: the client's pathname
 * builder (IdeaChatInput), the presign route's allowed prefix, and the parse
 * route's consume-time check. The consume check MUST use this (not the
 * namespace root) so legacy `onboarding-materials/{sessionHash}/` pathnames
 * can never be submitted cross-session (PR #540 review).
 */
export const ONBOARDING_DECK_CLIENT_PATH_PREFIX =
  `${UPLOAD_NAMESPACES["onboarding-decks"].pathPrefix}client/` as const;
